import {buildUrl} from "../utils/apiCall";
import {IComment, ICommentResponse, ICommentsApiOptions, ICommentsResponse, IRate} from "./types";
import {delay} from "redux-saga";
import {randomInt} from "../utils/random";

function commentResponseToComment(commentResponse: ICommentResponse): IComment | undefined {
    if (commentResponse == null)
        return;

    return {
        id: commentResponse.Id,
        createdOn: new Date(commentResponse.CreatedOn),
        parentId: commentResponse.ParentId,
        text: commentResponse.Text,
        rate: commentResponse.Rate,
        rating: commentResponse.Rating,
        user: {
            id: commentResponse.UserId,
            name: commentResponse.UserName,
            image: commentResponse.UserImage
        }
    }
}

let currentEndpoint = randomInt(100);

export default class CommentsApi {
    private readonly options: ICommentsApiOptions;

    public constructor(options: ICommentsApiOptions) {
        this.options = {
            ...options,
            api: {
                ...options.api,
                endpoints: options.api.endpoints.map(endpoint => endpoint.replace(/\/+$/, ""))
            }
        }
    }

    public async getComment(key: string, commentId: number): Promise<IComment | undefined> {
        const parameters = {
            key: key,
            commentId: commentId
        };

        const response = await this.send<ICommentResponse>("GET", "/Comment", parameters);

        return commentResponseToComment(response);
    }

    public async get(key: string, page: number, pageSize: number, sortAsc: boolean, includeFeaturedComments?: boolean): Promise<IComment[]> {
        const parameters = {
            key: key,
            page: page,
            size: pageSize,
            sortAsc: sortAsc,
            includeFeaturedComments: !!includeFeaturedComments
        };

        const response = await this.send<ICommentsResponse>("GET", "/Comments", parameters);
        if (!response.Comments)
            return [];

        return response.Comments.map(commentResponseToComment).filter(Boolean) as IComment[];
    }

    public async rate(key: string, commentId: number, rate: "up" | "down"): Promise<IRate> {
        const parameters = {
            key: key,
            commentId: commentId,
            rate: rate
        };

        const response = await this.send<{Id: number; Rating: number;}>("POST", "/Rate", parameters);

        return {
            id: response.Id,
            rating: response.Rating
        };
    }

    public async post(key: string, text: string, contentId: string, parentId?: number,  attributes?: Record<string, any>): Promise<IComment | undefined> {
        const {language, userName, userImage} = this.options;

        let parameters = {
            key: key,
            parentId: parentId,
            userName: userName,
            userImage: userImage,
            text: text,
            contentId: contentId,
            language: language,
            attributes: attributes
        };

        const response = await this.send<ICommentResponse>("POST", "/Comment", parameters);

        return commentResponseToComment(response);
    }

    private async send<T>(method: "POST" | "GET", url: string, parameters: Object): Promise<T> {
        let lastError: Error | void;

        const {userId, api} = this.options;

        parameters = {
            apiKey: api.apiKey,
            userId: userId,
            ...parameters
        };

        url = buildUrl(this.getCurrentApiEndpoint(), url, method === "GET" ? parameters: {});

        for (let i = 0; i < api.retryCount; i++) {
            try {
                const response = await fetch(url, {
                    method: method,
                    mode: "cors",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json"
                    },
                    body: method === "POST"
                        ? JSON.stringify(parameters)
                        : undefined
                });

                if (response.ok) {
                    return response.json();
                }

                lastError = new Error("Invalid api response");
            }
            catch (error) {
                lastError = error;
            }

            await delay(api.retryTimeout);
        }

        throw lastError || new Error("Failed to send request");
    }

    private getCurrentApiEndpoint() {
        return this.options.api.endpoints[currentEndpoint % this.options.api.endpoints.length];
    }
}